/**
 * Von Diamonds API Integration Module
 * Minimal implementation for dynamic pricing with Prestige theme
 */

class VonDiamondsAPI {
  static ENDPOINTS = {
    DIAMOND_SYNC: 'https://shop.vondiamonds.com/api/diamond-feed/sync/',
    PRODUCT_UPDATE: 'https://custom.vondiamonds.com/updateProduct.php'
  };

  static cache = new Map();
  static cacheTimeout = 5 * 60 * 1000; // 5 minutes

  /**
   * Sync diamond product pricing from external feed
   * @param {number} productId - Shopify product ID
   * @returns {Promise<Object|null>} - API response data or null if failed
   */
  static async syncDiamondPricing(productId) {
    if (!productId) return null;

    // Check cache first
    const cacheKey = `diamond_sync_${productId}`;
    const cachedData = this.cache.get(cacheKey);
    
    if (cachedData && Date.now() - cachedData.timestamp < this.cacheTimeout) {
      return cachedData.data;
    }

    try {
      const response = await fetch(`${this.ENDPOINTS.DIAMOND_SYNC}${productId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result && result.status && result.data) {
        // Cache successful response
        this.cache.set(cacheKey, {
          data: result.data,
          timestamp: Date.now()
        });
        
        return result.data;
      }

      return null;
    } catch (error) {
      console.warn('Von Diamonds API: Failed to sync diamond pricing:', error);
      return null;
    }
  }

  /**
   * Format price for display using Shopify's money formatting
   * @param {number} priceInCents - Price in cents
   * @returns {string} - Formatted price string
   */
  static formatPrice(priceInCents) {
    if (typeof priceInCents !== 'number' || priceInCents < 0) {
      return '';
    }

    // Use Shopify's global money formatting if available
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(priceInCents, window.themeVariables?.settings?.moneyFormat || '${{amount}}');
    }

    // Fallback formatting
    const dollars = (priceInCents / 100).toFixed(2);
    return `$${dollars}`;
  }

  /**
   * Calculate discount percentage
   * @param {number} originalPrice - Original price in cents
   * @param {number} salePrice - Sale price in cents
   * @returns {number} - Discount percentage (0-100)
   */
  static calculateDiscountPercentage(originalPrice, salePrice) {
    if (!originalPrice || originalPrice <= salePrice) {
      return 0;
    }
    
    return Math.round(((originalPrice - salePrice) / originalPrice) * 100);
  }

  /**
   * Update product pricing in the DOM
   * @param {Object} priceData - Price data from API
   * @param {HTMLElement} priceContainer - Container element with price elements
   */
  static updatePriceDisplay(priceData, priceContainer) {
    if (!priceData || !priceContainer) return;

    const { price, original_price } = priceData;
    
    if (!price) return;

    const priceInCents = Math.round(parseFloat(price) * 100);
    const originalPriceInCents = original_price ? Math.round(parseFloat(original_price) * 100) : null;

    // Update main price
    const salePriceElement = priceContainer.querySelector('sale-price');
    if (salePriceElement) {
      const formattedPrice = this.formatPrice(priceInCents);
      salePriceElement.innerHTML = salePriceElement.innerHTML.replace(
        /\$[\d,]+\.?\d*/g, 
        formattedPrice.split('.')[0] // Remove cents for Von Diamonds format
      );

      // Update classes for sale pricing
      if (originalPriceInCents && originalPriceInCents > priceInCents) {
        salePriceElement.className = salePriceElement.className
          .replace(/text-subdued/g, 'text-on-sale');
      }
    }

    // Update compare at price
    const compareAtPriceElement = priceContainer.querySelector('compare-at-price');
    if (originalPriceInCents && originalPriceInCents > priceInCents) {
      if (compareAtPriceElement) {
        const formattedOriginalPrice = this.formatPrice(originalPriceInCents);
        compareAtPriceElement.innerHTML = compareAtPriceElement.innerHTML.replace(
          /\$[\d,]+\.?\d*/g,
          formattedOriginalPrice.split('.')[0]
        );
        compareAtPriceElement.style.display = '';
      }
    } else if (compareAtPriceElement) {
      compareAtPriceElement.style.display = 'none';
    }

    // Dispatch custom event for other components
    priceContainer.dispatchEvent(new CustomEvent('vondiamonds:price-updated', {
      detail: {
        price: priceInCents,
        originalPrice: originalPriceInCents,
        discountPercentage: this.calculateDiscountPercentage(originalPriceInCents, priceInCents)
      },
      bubbles: true
    }));
  }

  /**
   * Check if product is a diamond product requiring dynamic pricing
   * @param {Object} product - Product data
   * @returns {boolean}
   */
  static isDiamondProduct(product) {
    if (!product) return false;
    
    // Check product type or tags for diamond identification
    return product.type === 'Loose Diamonds' || 
           (product.tags && product.tags.includes('d-feed')) ||
           (product.tags && product.tags.includes('new-dfeed'));
  }

  /**
   * Initialize dynamic pricing for a product
   * @param {number} productId - Shopify product ID
   * @param {Object} productData - Product data object
   */
  static async initializeProductPricing(productId, productData) {
    if (!this.isDiamondProduct(productData)) {
      return;
    }

    const priceContainer = document.querySelector('price-list');
    if (!priceContainer) {
      console.warn('Von Diamonds API: Price container not found');
      return;
    }

    try {
      const apiData = await this.syncDiamondPricing(productId);
      if (apiData) {
        this.updatePriceDisplay(apiData, priceContainer);
      }
    } catch (error) {
      console.warn('Von Diamonds API: Failed to initialize dynamic pricing:', error);
    }
  }
}

// Auto-initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  // Get product data from page
  const productJson = document.querySelector('[data-product-json]');
  if (productJson) {
    try {
      const productData = JSON.parse(productJson.textContent);
      if (productData && productData.id) {
        VonDiamondsAPI.initializeProductPricing(productData.id, productData);
        
        // Listen for Prestige variant change events
        VonDiamondsAPI.initializeVariantListener(productData.id, productData);
      }
    } catch (error) {
      console.warn('Von Diamonds API: Could not parse product data');
    }
  }
});

/**
 * Initialize listener for Prestige variant change events
 * @param {number} productId - Shopify product ID
 * @param {Object} productData - Product data object
 */
VonDiamondsAPI.initializeVariantListener = function(productId, productData) {
  if (!this.isDiamondProduct(productData)) {
    return;
  }

  // Listen for Prestige's variant:change event
  document.addEventListener('variant:change', async (event) => {
    const { variant } = event.detail;
    
    if (!variant) return;
    
    // For diamond products, refresh pricing when variant changes
    try {
      const apiData = await this.syncDiamondPricing(productId);
      if (apiData) {
        const priceContainer = document.querySelector('price-list');
        if (priceContainer) {
          this.updatePriceDisplay(apiData, priceContainer);
        }
      }
    } catch (error) {
      console.warn('Von Diamonds API: Failed to update pricing on variant change:', error);
    }
  });

  // Listen for Prestige's product:rerender event for price container updates
  document.addEventListener('product:rerender', async (event) => {
    // Small delay to ensure DOM is updated
    setTimeout(async () => {
      try {
        const apiData = await this.syncDiamondPricing(productId);
        if (apiData) {
          const priceContainer = document.querySelector('price-list');
          if (priceContainer) {
            this.updatePriceDisplay(apiData, priceContainer);
          }
        }
      } catch (error) {
        console.warn('Von Diamonds API: Failed to update pricing on product rerender:', error);
      }
    }, 100);
  });
};

// Export for use in other scripts
window.VonDiamondsAPI = VonDiamondsAPI;